import { useState } from 'react';
import type { SpotCategory } from '@ridehub/shared';
import { CATEGORIES } from '@ridehub/shared';

type SortOrder = 'distance' | 'likes' | 'checkins' | 'rank';

interface SpotFilterPanelProps {
  category?: SpotCategory;
  sort: SortOrder;
  onCategoryChange: (category?: SpotCategory) => void;
  onSortChange: (sort: SortOrder) => void;
}

const SORT_OPTIONS: { value: SortOrder; label: string }[] = [
  { value: 'distance', label: '距離順' },
  { value: 'likes', label: 'いいね順' },
  { value: 'checkins', label: 'チェックイン順' },
  { value: 'rank', label: 'ランク順' },
];

export function SpotFilterPanel({
  category,
  sort,
  onCategoryChange,
  onSortChange,
}: SpotFilterPanelProps) {
  const [categoryOpen, setCategoryOpen] = useState(false);

  const selectedLabel = category
    ? (CATEGORIES.find((c) => c.value === category)?.label ?? 'カテゴリ')
    : 'すべて';

  const handleCategorySelect = (value?: SpotCategory) => {
    onCategoryChange(value);
    setCategoryOpen(false);
  };

  return (
    <div className="bg-white border-b border-gray-200 p-3 flex flex-col gap-3">
      {/* カテゴリアコーディオン */}
      <div>
        <button
          onClick={() => setCategoryOpen((prev) => !prev)}
          className="flex items-center gap-2 w-full px-3 py-1.5 rounded-full text-sm bg-gray-100 text-gray-700"
        >
          <span className="font-medium text-xs text-gray-400">カテゴリ</span>
          <span className={category ? 'text-primary font-medium' : ''}>{selectedLabel}</span>
          <svg
            className={`ml-auto w-4 h-4 text-gray-400 transition-transform ${categoryOpen ? 'rotate-180' : ''}`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {categoryOpen && (
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              onClick={() => handleCategorySelect(undefined)}
              className={[
                'px-3 py-1.5 rounded-full text-sm transition-colors',
                !category ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600',
              ].join(' ')}
            >
              すべて
            </button>
            {CATEGORIES.map((cat) => (
              <button
                key={cat.value}
                onClick={() => handleCategorySelect(cat.value)}
                className={[
                  'px-3 py-1.5 rounded-full text-sm transition-colors',
                  category === cat.value ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600',
                ].join(' ')}
              >
                {cat.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* 並び順 */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {SORT_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => onSortChange(opt.value)}
            className={[
              'flex-shrink-0 px-3 py-1 rounded text-sm transition-colors',
              sort === opt.value ? 'text-primary font-medium' : 'text-gray-500',
            ].join(' ')}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}
